import { InvoiceDTO } from '../../services/apiClient';
import { TableAction } from '../common/Table';
import { Eye, FileText, UserCog, Edit3, Undo, Trash2, Send } from 'lucide-react';
import { InvoiceStatusConstants } from '../../constants/InvoiceStatusConstants';

interface InvoiceActionHandlers {
  onViewMember?: (invoice: InvoiceDTO) => void;
  onLoginAsMember?: (invoice: InvoiceDTO) => void;
  onViewDocument?: (invoice: InvoiceDTO) => void;
  onEditForPayment?: (invoice: InvoiceDTO) => void;
  onRevertInvoice?: (invoice: InvoiceDTO) => void;
  onResend?: (invoice: InvoiceDTO) => void;
  onDelete?: (invoice: InvoiceDTO) => void;
}

const createInvoiceActions =
  (handlers: InvoiceActionHandlers) =>
    (row: InvoiceDTO): TableAction[] => {
      const actions: TableAction[] = [];

      if (handlers.onViewMember) {
        // View Member
        actions.push({
          label: 'View Member',
          onClick: () => handlers.onViewMember!(row),
          icon: <Eye className="w-4 h-4" />,
        });
      }

      // Login as Member
      if (row.member?.userID && handlers.onLoginAsMember) {
        actions.push({
          label: 'Login as Member',
          onClick: () => handlers.onLoginAsMember!(row),
          icon: <UserCog className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to login as this member?',
        });
      }

      if (handlers.onViewDocument) {
        // View Sales Order / Invoice
        actions.push({
          label: row.invoiceNumber ? 'View Invoice' : 'View Sales Order',
          onClick: () => handlers.onViewDocument!(row),
          icon: <FileText className="w-4 h-4" />,
        });
      }

      // Resend Invoice/Payment Notice - only show if has invoiceNumber or salesOrderNumber
      if (handlers.onResend && (row.invoiceNumber || row.salesOrderNumber)) {
        actions.push({
          label: row.invoiceNumber ? 'Resend Invoice' : 'Resend Sales Order',
          onClick: () => handlers.onResend!(row),
          icon: <Send className="w-4 h-4" />,
          confirmMessage: `Are you sure you want to resend this ${row.invoiceNumber ? 'invoice' : 'sales order'} email?`,
        });
      }

      // Edit Renewal - only show if not in INVOICED status
      if (handlers.onEditForPayment && row.invoiceStatusId?.toLowerCase() !== InvoiceStatusConstants.INVOICED.toLowerCase()) {
        actions.push({
          label: 'Edit Renewal',
          onClick: () => handlers.onEditForPayment!(row),
          icon: <Edit3 className="w-4 h-4" />,
        });
      }

      // Revert Invoice - only show for invoices with INVOICED status
      if (handlers.onRevertInvoice && row.invoiceStatusId?.toLowerCase() === InvoiceStatusConstants.INVOICED.toLowerCase()) {
        actions.push({
          label: 'Revert Invoice',
          onClick: () => handlers.onRevertInvoice!(row),
          className: 'text-red-600 hover:text-red-700',
          icon: <Undo className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to revert this invoice? This will undo the payment and reset the member\'s paid-up date.',
        });
      }

      // Delete Invoice
      if (handlers.onDelete) {
        actions.push({
          label: 'Delete Record',
          onClick: () => handlers.onDelete!(row),
          className: 'text-red-600 hover:text-red-700',
          icon: <Trash2 className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to delete this invoice?',
        });
      }

      return actions;
    };

export default createInvoiceActions;
